import { APP_CONFIG } from '@/config/app.config';

const { symbol, locale } = APP_CONFIG.currency;

/** "£4.5m" — compact money label used for prices, bank and team value. */
export const formatMoney = (millions: number): string =>
  `${symbol}${millions.toFixed(1)}m`;

/** "1,248" — thousands-separated, locale aware. */
export const formatNumber = (value: number): string =>
  new Intl.NumberFormat(locale).format(value);

/**
 * "1,248", or "—" for a rank of 0. The API reports 0 for a manager who has not
 * been ranked yet — no team, or no settled fixture — not first-equal-to-last.
 */
export const formatRank = (rank: number): string => (rank > 0 ? formatNumber(rank) : '—');

/**
 * A gameweek's state as a short phrase, e.g. "GW06 is open", "GW38 finished".
 *
 * Never "open" once the deadline has passed or the round is over, and no
 * number at all when the API did not give a real one (0).
 */
export function describeGameweek(gameweek: { id: number; status: string }): string {
  const label = gameweek.id > 0 ? `GW${formatGameweek(gameweek.id)}` : null;

  switch (gameweek.status) {
    case 'open':
      return label ? `${label} is open` : 'Gameweek open';
    case 'settled':
      return label ? `${label} finished` : 'Gameweek finished';
    default:
      return label ? `${label} is under way` : 'Gameweek under way';
  }
}

/** Gameweek 5 -> "05". */
export const formatGameweek = (gw: number): string => String(gw).padStart(2, '0');

/** "Samuel Cole" -> "Cole". Used on space-constrained pitch cards. */
export const surnameOf = (fullName: string): string =>
  fullName.split(' ').at(-1) ?? fullName;

/** "Alex Morgan" -> "AM". */
export const initialsOf = (fullName: string): string =>
  fullName
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
